import React, { Fragment, Element } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { kebabCase } from 'lodash'
import { FieldArray } from 'redux-form'
import shuffle from 'helpers/shuffle'
import Mixin from 'components/common/Mixin'
import Drawer from 'components/common/Drawer'
import Columns from 'components/common/Columns/Columns'
import { injectProps } from 'helpers/utils'
import { SectionWrapper, ErrorMessage, ActionBarWrapper } from '../../../Stepper/components/common'
import { Heading } from '../common'
import { APPOINTMENT_CHECKBOX_INPUT_COLOR_GREEN, MORE_SERVICES_ID } from '../../constants'
import StepperActionBar from '../../../Stepper/components/StepperActionBar'
import MyStoreBar from './MyStoreBar'
import PopularServices from './PopularServices'
import ServicesDrawer from './ServicesDrawer'
import ServicesDrawerTitle from './ServicesDrawerTitle'
import ServicesComment from '../ServicesComment'
import ActivePromotions from './ActivePromotions'
import { ACTIVE_PROMOTIONS_FIELD_NAME } from 'constant'

const StoreAndServices = ({
  BOTStoreLocation,
  currentStepIndex,
  error,
  handleSubmit,
  handleEditClick,
  handleCloseClick,
  popularServiceItems,
  servicesCategories,
  servicesDrawerItems,
  submitFailed,
  showStoreDetails,
  submitAttemptFailed,
  handleMoreServicesClick,
  selectedServices,
  drawerButtonRef,
  categoryQueryParam,
  promoExcluded,
  appointmentNotes,
  ...props
}) =>
  <form onSubmit={ handleSubmit }>
    <StyledSectionWrapper>
      {
        showStoreDetails ?
          injectProps(BOTStoreLocation, {
            showChangeStoreButton: true,
            threeStepper: true,
            noHeading: true,
            handleEditClick,
            handleCloseClick
          }) :
          <MyStoreBar { ...props } />
      }
      <Heading data-at-appointment-headline>1. Select Service</Heading>

      <FieldArray
        name={ ACTIVE_PROMOTIONS_FIELD_NAME }
        component={ ActivePromotions }
        promotionsDetails />

      <div>
        <PopularServices popularServiceItems={ popularServiceItems } />
      </div>
      <div>
        <StyledDrawer
          withHeading
          preventHandleBlur
          setDrawerButtonRef={ drawerButtonRef }
          drawerProps={{
            CustomDrawerTitle: ServicesDrawerTitle,
            title: 'More Services',
            expandedTitle: 'Less Services',
            hideExpanderIcon: true,
            drawerIcon: 'arrow-bright-green-right',
            altIcon: 'arrow-icon-full-bright-green-down',
            drawerButtonHandler: handleMoreServicesClick
          }}>
          <StyledColumns adaptive width="50% 50%" id={ MORE_SERVICES_ID }>
            {
              shuffle.halfs(servicesCategories).map((serviceCategoriesSplit, i) =>
                <Fragment key={ i }>
                  {
                    serviceCategoriesSplit.map(({ categoryName, iconName }, j) =>
                      <ServicesDrawer
                        key={ j }
                        title={ categoryName }
                        iconName={ iconName }
                        index={ j }
                        categoryServices={ servicesDrawerItems[i][j] }
                        selectedServices={ selectedServices }
                        inputColor={ APPOINTMENT_CHECKBOX_INPUT_COLOR_GREEN }
                        isPreExpand={ kebabCase(categoryName) === categoryQueryParam } />
                    )
                  }
                </Fragment>
              )
            }
          </StyledColumns>
        </StyledDrawer>
      </div>
      { Boolean(selectedServices.length) && <ServicesComment value={ appointmentNotes } /> }
      <ActionBarWrapper condensedBottom>
        <StepperActionBar
          hasNextPage
          isPrevPageVisible
          alignRight
          currentStepIndex={ currentStepIndex } />

        <ErrorMessage
          role="alert"
          aria-live="assertive"
          alignRight
          aria-label={ error }>
          { ((submitFailed || submitAttemptFailed) && error) || '' }
        </ErrorMessage>
      </ActionBarWrapper>
    </StyledSectionWrapper>
  </form>

StoreAndServices.propTypes = {
  appointmentNotes: PropTypes.string,
  BOTStoreLocation: PropTypes.element,
  categoryQueryParam: PropTypes.string,
  currentStepIndex: PropTypes.number,
  drawerButtonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  error: PropTypes.string,
  handleCloseClick: PropTypes.func,
  handleEditClick: PropTypes.func,
  handleMoreServicesClick: PropTypes.func,
  handleSubmit: PropTypes.func,
  popularServiceItems: PropTypes.arrayOf(PropTypes.shape()),
  promoExcluded: PropTypes.bool,
  scrollToAnchorHandler: PropTypes.func,
  selectedServices: PropTypes.arrayOf(PropTypes.string),
  servicesCategories: PropTypes.arrayOf(PropTypes.shape()),
  servicesDrawerItems: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape()))
  ),
  showStoreDetails: PropTypes.bool,
  submitAttemptFailed: PropTypes.bool,
  submitFailed: PropTypes.bool
}

export default StoreAndServices

const StyledSectionWrapper = styled(SectionWrapper)`
  ${Mixin.responsive('padding', '10px 100px 0', '20px 15px')}
`

const StyledDrawer = styled(Drawer)`
  ${Mixin.responsive('padding', '10px 0 0', '10px 0')}
  border: none;
`

const StyledColumns = styled(Columns)`
  & [data-column] {
    padding: 0 10px;
  }
`
